import {
    generateID,
    getJsonKeyFromField,
    getV2ValueWP,
    tryParseJSON,
} from '@eventr/common'
import { MainButton } from '@eventr/common-storybook'
import { SetStateAction, useEffect, useMemo, useState } from 'react'
import { IEditComponent } from '../../../data/Data'
import i18n from '../../../i18n/i18next'
import { bs } from '../../../state/bound.store'
import { isUserPartOfUsersWithTags } from '../../../v2/v2Utils'
import { useEditSectionContext } from '../editSectionProvider'

interface IJoinLeaveButtonProps {
    options: IEditComponent
    _id?: string
}

/// I NEED TO FIX THIS
export const JoinLeaveButton = ({ options, _id }: IJoinLeaveButtonProps) => {
    const { section, values, getValue, updateValues, isNewItem } =
        useEditSectionContext()

    const [isJoined, setIsJoined] = useState(false)
    const [listIdUserJoined, setListIdUserJoined] = useState<number>(0)
    const [joinOptions, setJoinOptions] = useState<any[]>([])

    useEffect(() => {
        if (options.settings.userListFieldId) {
            resolveSingleUserList()
        }
        if (options.settings.multiUserLists) {
            resolveMultiUserLists()
        }
    }, [section, options, values])

    const resolveSingleUserList = () => {
        const userList = getValue(
            bs().getFieldById(options.settings.userListFieldId) ?? (() => { throw new Error("Field not found") })(),
            _id
        )

        if (options.settings?.childUserListId) {
            const childField = bs().getFieldById(
                options.settings?.childUserListId
            )
            if (!childField) {
                throw new Error("Child field not found");
            }
            const jsonKey = getJsonKeyFromField(childField);
            const isJoined = userList?.some((parent: any) => {
                const usersWithTag = tryParseJSON(
                    getV2ValueWP(jsonKey, parent),
                    []
                )
                return isUserPartOfUsersWithTags(
                    bs().getCurrentUser() ?? (() => { throw new Error("Current user not found") })(),
                    usersWithTag
                )
            })
            setIsJoined(isJoined)
        } else {
            const isJoined = isUserPartOfUsersWithTags(
                bs().getCurrentUser() ?? (() => { throw new Error("Current user not found") })(),
                userList
            )
            setIsJoined(isJoined)
        }
    }

    const resolveMultiUserLists = () => {
        const _joinOptions: SetStateAction<any[]> = []
        options.settings.multiUserLists.forEach((list: any) => {
            _joinOptions.push({
                label: list.optionName,
                onClick: () => {
                    handleJoinSingleUserList(list.fieldId)
                },
            })
            const userList = getValue(
                bs().getFieldById(list.fieldId) ?? (() => { throw new Error("Field not found") })(),
                _id
            )
            const isJoined = isUserPartOfUsersWithTags(
                bs().getCurrentUser() ?? (() => { throw new Error("Current user not found") })(),
                userList
            )
            if (isJoined) {
                setListIdUserJoined(list.fieldId)
                setIsJoined(isJoined)
            }
        })
        setJoinOptions(_joinOptions)
    }

    const joinText = useMemo(() => {
        if (section?.id) {
            return i18n.t('edit-itinerary.joinButtonText.' + section.id)
        } else {
            return i18n.t('edit-itinerary.joinButtonText.' + 'default')
        }
    }, [section?.id])

    const leaveText = useMemo(() => {
        if (section?.id) {
            return i18n.t('edit-itinerary.leaveButtonText.' + section.id)
        } else {
            return i18n.t('edit-itinerary.leaveButtonText.' + 'default')
        }
    }, [section?.id])

    const handleLeave = () => {
        if (options.settings.multiUserLists) {
            handleLeaveMultiUserList()
            return
        }
        const userList = getValue(
            bs().getFieldById(options.settings.userListFieldId) ?? (() => { throw new Error("Field not found") })(),
            _id
        )
        const currentUser = bs().getCurrentUser()
        if (options.settings?.childUserListId) {
            const childField = bs().getFieldById(
                options.settings?.childUserListId
            )
            const jsonKey = getJsonKeyFromField(childField)

            const parentObj = userList?.find((parent) => {
                const usersWithTag = tryParseJSON(
                    getV2ValueWP(jsonKey, parent),
                    []
                )
                return isUserPartOfUsersWithTags(
                    bs().getCurrentUser(),
                    usersWithTag
                )
            })

            if (!parentObj) return
            parentObj[jsonKey] = parentObj[jsonKey].filter(
                (u: any) => u.option !== currentUser.id
            )
            if (parentObj[jsonKey].length === 0) {
                //remove entire thing
                const newUsers = userList.filter(
                    (u: any) => u._id !== parentObj._id
                )
                updateValues({
                    value: newUsers,
                    field: bs().getFieldById(options.settings.userListFieldId),
                    _id,
                })
            } else {
                updateValues({
                    value: userList,
                    field: bs().getFieldById(options.settings.userListFieldId),
                    _id,
                })
            }

            setIsJoined(false)
        } else {
            const newUsers = userList.filter(
                (u: any) => u.option !== currentUser.id
            )
            updateValues({
                value: newUsers,
                field: bs().getFieldById(options.settings.userListFieldId),
                _id,
            })
            setIsJoined(false)
        }
    }

    const handleLeaveMultiUserList = () => {
        if (listIdUserJoined === 0) return

        const userList = getValue(bs().getFieldById(listIdUserJoined), _id)
        const currentUser = bs().getCurrentUser()
        const newUsers = userList.filter(
            (u: any) => u.option !== currentUser.id
        )
        updateValues({
            value: newUsers,
            field: bs().getFieldById(listIdUserJoined),
            _id,
        })
        setListIdUserJoined(0)
        setIsJoined(false)
    }

    const handleJoin = () => {
        if (options.settings.multiUserLists) {
            handleJoinMultiUserList()
            return
        }
        handleJoinSingleUserList(
            options.settings.userListFieldId,
            options.settings?.childUserListId
        )
    }

    const handleJoinSingleUserList = (
        userListFieldId: number,
        childUserListId?: number
    ) => {
        const userList = getValue(bs().getFieldById(userListFieldId), _id)
        const currentUser = bs().getCurrentUser()

        if (childUserListId) {
            const childField = bs().getFieldById(childUserListId)
            const jsonKey = getJsonKeyFromField(childField)
            const newUsers = [
                ...userList,
                {
                    [jsonKey]: [{ option: currentUser.id, type: 'User' }],
                    _id: generateID(
                        bs().selectedItinID,
                        section.id,
                        userListFieldId
                    ),
                },
            ]
            updateValues({
                value: newUsers,
                field: bs().getFieldById(userListFieldId),
                _id,
            })
            setIsJoined(true)
        } else {
            const newUsers = [
                ...userList,
                { option: currentUser.id, type: 'User' },
            ]
            updateValues({
                value: newUsers,
                field: bs().getFieldById(userListFieldId),
                _id,
            })
            setIsJoined(true)
        }
    }

    const handleJoinMultiUserList = () => {
        // need to fix this
        // dialog.openMultiSelectDialog({
        //     options: joinOptions,
        //     onClose: () => {
        //         dialog.closeDialogs()
        //     },
        // })
    }
    if (isNewItem(_id)) return null

    return (
        <>
            {isJoined && (
                <MainButton
                    id={`${leaveText}-button`}
                    onClick={handleLeave}
                    icon="Subtract"
                >
                    {leaveText}
                </MainButton>
            )}
            {!isJoined && (
                <MainButton
                    id={`${joinText}-button`}
                    onClick={handleJoin}
                    icon="Add"
                >
                    {joinText}
                </MainButton>
            )}
        </>
    )
}
